import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { IconButton } from '@mui/material';

const fullscreenButtonStyle: React.CSSProperties = {
  position: 'absolute',
  top: '10px',
  right: '10px',
  zIndex: 2000,
  background: '#FFF',
  borderRadius: '50%',
};

interface GLTFViewerProps {
  modelPath?: string;
  loading: boolean;
  onError: () => void;
  width: number;
}

const GLTFViewer: React.FC<GLTFViewerProps> = ({ modelPath, loading, width, onError }) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const updateRendererSize = () => {
    if (mountRef.current && rendererRef.current && cameraRef.current) {
      const w = isFullscreen ? window.innerWidth : mountRef.current.offsetWidth;
      const h = isFullscreen ? window.innerHeight : 800;
      rendererRef.current.setSize(w, h);
      cameraRef.current.aspect = w / h;
      cameraRef.current.updateProjectionMatrix();
    }
  };

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await mountRef.current?.requestFullscreen();
    } else {
      await document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      updateRendererSize();
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (loading) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#f5f5f5');

    const containerWidth = isFullscreen ? window.innerWidth : width;
    const containerHeight = isFullscreen ? window.innerHeight : 800;

    const camera = new THREE.PerspectiveCamera(75, containerWidth / containerHeight, 0.1, 1000);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(containerWidth, containerHeight);
    rendererRef.current = renderer;

    mountRef.current?.appendChild(renderer.domElement);

    const clock = new THREE.Clock();
    let mixer: THREE.AnimationMixer | undefined;
    let model: THREE.Object3D | null = null;

    camera.position.set(5, 5, 5);
    camera.lookAt(0, 0, 0);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 3);
    directionalLight.position.set(5, 5, 5);
    scene.add(directionalLight);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;

    const loader = new GLTFLoader();
    if (modelPath) {
      loader.load(
        modelPath,
        (gltf) => {
          model = gltf.scene;
          scene.add(model);

          if (gltf.animations.length > 0) {
            mixer = new THREE.AnimationMixer(model);
            gltf.animations.forEach((clip) => {
              mixer?.clipAction(clip).play();
            });
          }
        },
        undefined,
        (error) => {
          console.error('Error loading GLTF model:', error);
          onError();
        }
      );
    }

    const animate = () => {
      requestAnimationFrame(animate);
      const delta = clock.getDelta();
      if (mixer) mixer.update(delta);
      controls.update();
      renderer.render(scene, camera);
    };

    const handleResize = () => updateRendererSize();

    window.addEventListener('resize', handleResize);
    animate();

    return () => {
      if (model) {
        scene.remove(model);
        model.traverse((child) => {
          if ((child as THREE.Mesh).isMesh) {
            const mesh = child as THREE.Mesh;
            mesh.geometry.dispose();
            if (Array.isArray(mesh.material)) {
              mesh.material.forEach((mat) => mat.dispose());
            } else {
              mesh.material.dispose();
            }
          }
        });
      }
      mountRef.current?.removeChild(renderer.domElement);
      renderer.dispose();
      window.removeEventListener('resize', handleResize);
    };
  }, [loading, width, modelPath, isFullscreen]);

  return (
    <>
      {loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: `${width}px`,
            height: '800px',
            fontSize: '18px',
            color: '#555',
            backgroundColor: '#f0f0f0',
            border: '1px solid #ccc',
            borderRadius: '8px',
          }}
        >
          Probíhá načítání modelu
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <div
            ref={mountRef}
            style={{
              width: isFullscreen ? '100vw' : `${width}px`,
              height: isFullscreen ? '100vh' : '800px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <IconButton onClick={toggleFullscreen} style={fullscreenButtonStyle} size="large">
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </div>
        </div>
      )}
    </>
  );
};

export default GLTFViewer;
